import * as signalR from '@microsoft/signalr';

const HUB_URL = import.meta.env.VITE_SIGNALR_URL
  ? `${import.meta.env.VITE_SIGNALR_URL}/hubs/notifications`
  : 'http://localhost:5000/hubs/notifications';

// Logger utility - only logs in development mode
const logger = {
  info: (message: string, ...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.info(`[SignalR] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.warn(`[SignalR] ${message}`, ...args);
    }
  },
  error: (message: string, ...args: unknown[]) => {
    // Always log errors
    console.error(`[SignalR] ${message}`, ...args);
  },
};

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();

  async start(accessToken: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      logger.info('Already connected');
      return;
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => accessToken,
        withCredentials: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
            return null; // Stop reconnecting
          }
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s
          return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 16000);
        },
      })
      .configureLogging(import.meta.env.DEV ? signalR.LogLevel.Information : signalR.LogLevel.Warning)
      .build();

    // Set up reconnection handling
    this.connection.onreconnecting((error) => {
      logger.warn('Reconnecting...', error);
      this.reconnectAttempts++;
    });

    this.connection.onreconnected((connectionId) => {
      logger.info('Reconnected:', connectionId);
      this.reconnectAttempts = 0;
      // Re-register all listeners after reconnection
      this.reRegisterListeners();
    });

    this.connection.onclose((error) => {
      logger.warn('Connection closed:', error);
      this.connection = null;
    });

    try {
      await this.connection.start();
      logger.info('Connected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      logger.error('Connection failed:', error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.listeners.clear();
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  // Subscribe to events
  on<T>(event: string, callback: (data: T) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const callbacks = this.listeners.get(event)!;
    callbacks.add(callback as (...args: unknown[]) => void);

    // Register with SignalR if connected
    if (this.connection) {
      this.connection.on(event, callback);
    }

    // Return unsubscribe function
    return () => {
      callbacks.delete(callback as (...args: unknown[]) => void);
      if (this.connection) {
        this.connection.off(event, callback);
      }
    };
  }

  // Invoke hub methods
  async invoke<T>(method: string, ...args: unknown[]): Promise<T> {
    if (!this.connection || !this.isConnected()) {
      throw new Error('SignalR not connected');
    }
    return await this.connection.invoke<T>(method, ...args);
  }

  // Subscribe to ticket updates
  async subscribeToTicket(ticketId: string): Promise<void> {
    await this.invoke('SubscribeToTicket', ticketId);
  }

  async unsubscribeFromTicket(ticketId: string): Promise<void> {
    await this.invoke('UnsubscribeFromTicket', ticketId);
  }

  // Subscribe to dashboard updates
  async subscribeToDashboard(): Promise<void> {
    await this.invoke('SubscribeToDashboard');
  }

  async unsubscribeFromDashboard(): Promise<void> {
    await this.invoke('UnsubscribeFromDashboard');
  }

  private reRegisterListeners(): void {
    if (!this.connection) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.connection!.on(event, callback);
      });
    });
  }
}

// Singleton instance
export const signalRService = new SignalRService();

// Event types
export interface TicketNotification {
  ticketId: string;
  ticketNumber: string;
  customerName: string;
  deviceName: string;
  status: string;
  technicianName?: string;
  timestamp: string;
}

export interface StatusChangeNotification {
  ticketId: string;
  ticketNumber: string;
  oldStatus: string;
  newStatus: string;
  changedBy?: string;
  timestamp: string;
}

export interface DashboardUpdateNotification {
  type: string;
  totalTickets?: number;
  pendingTickets?: number;
  completedToday?: number;
  revenueToday?: number;
  timestamp: string;
}

export interface UserNotification {
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  actionUrl?: string;
  timestamp: string;
}

export interface LowStockNotification {
  partId: string;
  partName: string;
  sku: string;
  currentStock: number;
  minimumStock: number;
  timestamp: string;
}
